<template>
  <div class="payroll-refunds-page">
    <!-- Page Header -->
    <div class="page-header">
      <h1 class="page-title">{{ t('Solicitações de Reembolso') }}</h1>
      <p class="page-description">
        {{ t('Gerencie as solicitações de reembolso de despesas pagas pelos operadores') }}
      </p>
    </div>

    <!-- Action Bar -->
    <div class="action-bar">
      <div class="actions">
        <AppButton
          data-testid="refunds-nova-solicitacao-btn"
          :label="t('Nova Solicitação')"
          icon="pi pi-plus"
          @click="openNewModal"
        />
      </div>
    </div>

    <!-- Refunds Table (always visible) -->
    <PayrollRefundsTable
      :refunds="refunds"
      :users-map="usersMap"
      :show-accept-decline="isManager"
      :can-edit="true"
      :can-delete="true"
      :is-loading="isLoading"
      @edit="handleEdit"
      @view="handleView"
      @accept="handleAccept"
      @decline="handleDecline"
      @delete="handleDelete"
      @refresh="loadRefunds"
      @view-gallery="handleViewGallery"
    />

    <!-- Image Gallery -->
    <ImageGallery
      v-if="showGallery"
      :images="galleryImages"
      :initial-index="galleryInitialIndex"
      :loading="false"
      :empty-message="t('Nenhuma imagem encontrada')"
      :show-thumbnails="false"
      :show-zoom-controls="true"
      :show-fullscreen-control="true"
      :show-delete-button="false"
      @close="handleCloseGallery"
    />

    <!-- Accept Wizard Modal -->
    <PayrollRefundsAcceptWizard
      v-if="showAcceptWizard"
      :visible="showAcceptWizard"
      :refund="selectedRefund"
      @update:visible="showAcceptWizard = $event"
      @completed="handleAcceptCompleted"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../../utils/DebugLogger';
import { eventBus } from '../../../eventBus';
import { permissionsService } from '../../../services/permissionsService';
import { usePayrollRefunds } from '../../../composables/usePayrollRefunds';
import { useConfirmAction } from '../../../composables/useConfirmAction';

type Id = number | string;

interface Refund {
  id: Id;
  pays_to?: Id | null;
  user_id?: Id | null;
  title?: string;
  amount?: number;
  status?: number;
  [key: string]: unknown;
}

interface UserMapEntry {
  name?: string;
  user_name?: string;
  pays_to?: unknown;
  funcionario_id?: unknown;
}

interface GalleryImage {
  id: string | Id;
  url: string;
  thumbnailUrl: string;
  label: string;
  type: string;
}

interface GalleryPayload {
  fileUrl: string;
  title: string;
  refundId?: string | null;
}

const { t } = useI18n();
const { listRefunds, deleteRefund, isLoading } = usePayrollRefunds();
const { confirmAction } = useConfirmAction();

// State
const refunds = ref<Refund[]>([]);
const showAcceptWizard = ref<boolean>(false);
const selectedRefund = ref<Refund | null>(null);
const usersMap = ref<Record<Id, UserMapEntry>>({});
const showGallery = ref<boolean>(false);
const galleryImages = ref<GalleryImage[]>([]);
const galleryInitialIndex = ref<number>(0);

// Computed
const isManager = computed((): boolean => {
  return permissionsService.isUserInGroups(['Full', 'Franqueado', 'Gerente']);
});

// Methods
const loadRefunds = async (): Promise<void> => {
  log('PayrollRefundsPage', 'loadRefunds', 'Loading refunds');
  try {
    const data = await listRefunds({}) as Refund[];
    refunds.value = data;
    loadUsers();
    log('PayrollRefundsPage', 'loadRefunds', 'Loaded refunds', { count: data.length });
  } catch (err) {
    log('PayrollRefundsPage', 'loadRefunds', 'Error', err);
    eventBus.emit('globalMessage.error', { message: t('Erro ao carregar reembolsos'), duration: 5000 });
  }
};

const loadUsers = (): void => {
  const map: Record<Id, UserMapEntry> = {};

  const currentUser = permissionsService.getUser() as { sub?: Id; name?: string } | null;
  if (currentUser?.sub) {
    map[currentUser.sub] = { name: currentUser.name, user_name: currentUser.name, pays_to: currentUser.sub };
  }

  const uniqueSubs = new Set<Id>();
  refunds.value.forEach(refund => {
    const sub = refund.pays_to || refund.user_id;
    if (sub) uniqueSubs.add(sub);
  });

  uniqueSubs.forEach(sub => {
    const user = permissionsService.getUserBySub(String(sub)) as { funcionario_nome?: string; name?: string; funcionario_id?: unknown } | null;
    if (user) {
      const name = user.funcionario_nome || user.name;
      if (name) {
        map[sub] = { name, user_name: name, pays_to: sub, funcionario_id: user.funcionario_id };
      }
    }
  });

  usersMap.value = map;
  log('PayrollRefundsPage', 'loadUsers', 'Users map built from permissionsService', {
    totalUsers: Object.keys(map).length,
    refundsProcessed: refunds.value.length,
    uniqueSubs: uniqueSubs.size
  });
};

const openNewModal = (): void => {
  log('PayrollRefundsPage', 'openNewModal', 'Opening new modal');
  eventBus.emit('modal.payroll-refunds.open', null);
};

const handleEdit = (refund: Refund): void => {
  log('PayrollRefundsPage', 'handleEdit', 'Editing refund', refund.id);
  eventBus.emit('modal.payroll-refunds.open', refund);
};

const handleView = (refund: Refund): void => {
  log('PayrollRefundsPage', 'handleView', 'Viewing refund', refund.id);
  eventBus.emit('modal.payroll-refunds.open', refund);
};

const handleAccept = (refund: Refund): void => {
  log('PayrollRefundsPage', 'handleAccept', 'Opening accept wizard', refund.id);
  selectedRefund.value = refund;
  showAcceptWizard.value = true;
};

const handleDecline = (refund: Refund): void => {
  log('PayrollRefundsPage', 'handleDecline', 'Opening decline modal for refund', refund.id);
  eventBus.emit('modal.payroll-refunds-decline.open', refund);
};

const handleDelete = (refund: Refund): void => {
  log('PayrollRefundsPage', 'handleDelete', 'Deleting refund', refund.id);
  confirmAction({
    message: t('Tem certeza que deseja excluir esta solicitação?'),
    header: t('Confirmação'),
    onAccept: async () => {
      const success = await deleteRefund(Number(refund.id));
      if (success) {
        eventBus.emit('globalMessage.success', { message: t('Solicitação excluída com sucesso'), duration: 3000 });
        loadRefunds();
      } else {
        eventBus.emit('globalMessage.error', { message: t('Erro ao excluir solicitação'), duration: 5000 });
      }
    }
  });
};

const handleAcceptCompleted = (): void => {
  log('PayrollRefundsPage', 'handleAcceptCompleted', 'Accept completed');
  showAcceptWizard.value = false;
  selectedRefund.value = null;
  loadRefunds();
};

const handleViewGallery = ({ fileUrl, title, refundId }: GalleryPayload): void => {
  log('PayrollRefundsPage', 'handleViewGallery', 'Opening image gallery', { fileUrl, title, refundId });
  galleryImages.value = [{
    id: refundId || 'receipt',
    url: fileUrl,
    thumbnailUrl: fileUrl,
    label: title || t('Comprovante'),
    type: 'receipt'
  }];
  galleryInitialIndex.value = 0;
  showGallery.value = true;
};

const handleCloseGallery = (): void => {
  log('PayrollRefundsPage', 'handleCloseGallery', 'Closing image gallery');
  showGallery.value = false;
  galleryImages.value = [];
  galleryInitialIndex.value = 0;
};

// Lifecycle
onMounted(() => {
  loadRefunds();
  eventBus.on('payroll-refunds.refresh', loadRefunds);
  eventBus.on('payroll-refunds.created', loadRefunds);
  eventBus.on('payroll-refunds.updated', loadRefunds);
});

onBeforeUnmount(() => {
  eventBus.off('payroll-refunds.refresh', loadRefunds);
  eventBus.off('payroll-refunds.created', loadRefunds);
  eventBus.off('payroll-refunds.updated', loadRefunds);
});
</script>

<style scoped>
.payroll-refunds-page {
  padding: 1.5rem;
}

.page-header {
  margin-bottom: 1.5rem;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
  margin: 0 0 0.5rem 0;
}

.page-description {
  color: var(--text-color-secondary);
  margin: 0;
}

.action-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
}

.actions {
  display: flex;
  gap: 0.5rem;
}
</style>
